import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe, ArrowRight, CheckCircle, Palette, Smartphone, Search, Zap, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageSEO } from "@/components/PageSEO";

export default function WebDesignPage() {
  return (
    <div data-testid="web-design-page" className="pt-16 lg:pt-20">
      <PageSEO
        title="Website Design & Development UK"
        description="Professional website design and development for UK businesses. Mobile-responsive, SEO-ready sites with hosting included. No build fees. Get a free quote."
        path="/website-design-uk"
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 hero-gradient relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-cyan-400 text-sm font-medium uppercase tracking-widest mb-4 block">Website Design</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
              Professional Website Design <span className="gradient-text">for UK Businesses</span>
            </h1>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed max-w-2xl">
              We build modern, fast, mobile-responsive websites that look great and convert visitors into customers. No upfront build fees — just a simple monthly plan that includes hosting, domain, and ongoing support.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/pricing">
                <Button className="btn-primary text-white font-medium px-8 py-3">
                  View Pricing <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-white/5 px-8 py-3">
                  Get a Quote
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why a Great Website Matters */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">Why Your Website Is Your Most Important Asset</h2>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-gray-400 leading-relaxed mb-4">
                  Your website is often the first impression a potential customer has of your business. In fact, 75% of consumers judge a company's credibility based on its website design. A slow, outdated, or poorly designed site drives visitors to your competitors before they ever pick up the phone.
                </p>
                <p className="text-gray-400 leading-relaxed mb-4">
                  At Your-IT-Solutions, based in Lowestoft, Suffolk, we design websites that work as hard as you do. Every site we build is optimised for speed, search engines, and conversions — whether your customers are in East Anglia or anywhere in the United Kingdom.
                </p>
                <p className="text-gray-400 leading-relaxed">
                  Unlike agencies that charge thousands upfront and disappear, we offer a simple monthly plan. Your website stays updated, secure, and performing at its best — and you get ongoing support from a team that actually knows your business.
                </p>
              </div>
              <div className="space-y-4">
                <div className="p-5 rounded-xl bg-gray-900/50 border border-gray-800">
                  <h3 className="text-white font-semibold mb-1">No Upfront Build Fees</h3>
                  <p className="text-gray-400 text-sm">Simple monthly plans starting from just £90/month. Hosting, domain, and support all included.</p>
                </div>
                <div className="p-5 rounded-xl bg-gray-900/50 border border-gray-800">
                  <h3 className="text-white font-semibold mb-1">SEO-Ready from Day One</h3>
                  <p className="text-gray-400 text-sm">Every site is built with clean code, proper meta tags, and fast load times to help you rank on Google.</p>
                </div>
                <div className="p-5 rounded-xl bg-gray-900/50 border border-gray-800">
                  <h3 className="text-white font-semibold mb-1">Ongoing Support & Updates</h3>
                  <p className="text-gray-400 text-sm">Content edits, security updates, and performance monitoring — we handle it all so you can focus on running your business.</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Build */}
      <section className="py-16 lg:py-24 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-12">What We Build</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <Palette size={28} className="text-cyan-500 mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">Custom Design</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Every website is designed from scratch to match your brand identity. No cookie-cutter templates — your site will be unique to your business.</p>
              </div>
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <Smartphone size={28} className="text-cyan-500 mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">Mobile-First Responsive</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Over 60% of web traffic comes from mobile devices. Your website will look and function perfectly on phones, tablets, and desktops.</p>
              </div>
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <Search size={28} className="text-cyan-500 mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">Built-In SEO</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Clean URL structures, optimised meta tags, fast loading speeds, and proper heading hierarchy to help your site rank on Google from launch.</p>
              </div>
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <Zap size={28} className="text-cyan-500 mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">Lightning Fast</h3>
                <p className="text-gray-400 text-sm leading-relaxed">We optimise every image, script, and asset for maximum performance. Fast sites rank higher and keep visitors engaged longer.</p>
              </div>
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <ShieldCheck size={28} className="text-cyan-500 mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">Secure & Maintained</h3>
                <p className="text-gray-400 text-sm leading-relaxed">SSL certificates, regular security updates, and ongoing monitoring keep your website safe and your customers confident.</p>
              </div>
              <div className="p-6 rounded-2xl bg-gray-900/50 border border-gray-800">
                <Globe size={28} className="text-cyan-500 mb-4" />
                <h3 className="text-white font-semibold text-lg mb-2">Hosting & Domain</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Everything included in your monthly plan. Domain registration, reliable hosting, and domain protection — no hidden costs.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-12 text-center">Our Web Design Process</h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4"><span className="text-cyan-400 font-bold">1</span></div>
                <h3 className="text-white font-semibold mb-2">Consultation</h3>
                <p className="text-gray-400 text-sm">We discuss your goals, brand, and target audience to plan the perfect website.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4"><span className="text-cyan-400 font-bold">2</span></div>
                <h3 className="text-white font-semibold mb-2">Design</h3>
                <p className="text-gray-400 text-sm">We create a custom design that reflects your brand and appeals to your customers.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4"><span className="text-cyan-400 font-bold">3</span></div>
                <h3 className="text-white font-semibold mb-2">Development</h3>
                <p className="text-gray-400 text-sm">We build your site with clean, fast code optimised for performance and SEO.</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4"><span className="text-cyan-400 font-bold">4</span></div>
                <h3 className="text-white font-semibold mb-2">Launch & Support</h3>
                <p className="text-gray-400 text-sm">We launch your site and provide ongoing maintenance, updates, and support.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-gray-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Start Building Your Website Today</h2>
            <p className="text-gray-400 mb-8 text-lg">
              Based in Lowestoft, Suffolk, we work with businesses across East Anglia and the UK. No upfront fees, no long-term contracts — just results.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/pricing">
                <Button className="btn-primary text-white font-medium px-8 py-3">View Our Plans <ArrowRight className="ml-2" size={18} /></Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-white/5 px-8 py-3">Get a Quote</Button>
              </Link>
            </div>
            <p className="text-gray-500 text-sm mt-6">
              Looking for local web design? See our <Link to="/website-design-east-anglia" className="text-cyan-400 hover:text-cyan-300">East Anglia web design</Link> page. We also provide <Link to="/ai-automation-agents-uk" className="text-cyan-400 hover:text-cyan-300">AI automation</Link> services.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
